/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import HotelMarketInsightChartsChartSolidgaugeLegend from 'lumen/components/hotel/market-insight/charts/chart-solidgauge-legend';
import { stackGaugeChartData } from 'lumen/utils/charts/chart-utils';
import {
  GAUGE_INNER_RADIUS,
  GAUGE_OUTER_RADIUS,
} from 'lumen/utils/constants/market-insight-constants';

// Note: we don't use all secondary colors here as we're using COLOR_MARKET_INSIGHT_CLOSED as fixed colors for the soldout part
const CHART_COLORS = [
  'var(--prism-color-chart-categorical-02)',
  'var(--prism-color-chart-categorical-03)',
  'var(--prism-color-chart-categorical-04)',
];

interface HotelMarketInsightChartsChartAvailabilitySignature {
  Args: {
    onUpdateChartReference: unknown;
    summary: unknown;
  };
}

export default class HotelMarketInsightChartsChartAvailability extends Component<HotelMarketInsightChartsChartAvailabilitySignature> {
  @service declare intl: Services['intl'];

  @computed('args.summary.{losRestrictions,soldOutPercentageNumber}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    // @ts-expect-error: Incorrect type
    const { soldOutPercentageNumber, losRestrictions } = this.args.summary;

    const data = [];

    // Handle soldout number
    data.push({
      name: this.intl.t('market_insight.availability.closed').toString(),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      originalY: soldOutPercentageNumber,
      color: 'var(--prism-color-chart-accent-primary-figure-default)',
    });

    // Handle LOS restriction data (if present)
    if (losRestrictions) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      losRestrictions
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .sort(
          // @ts-expect-error: Incorrect type
          (losRestrictionA, losRestrictionB) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            losRestrictionA.los - losRestrictionB.los,
        )
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .forEach((losRestriction, index) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const roundedValue = Math.round(losRestriction.value * 100);
          data.push({
            name: this.intl
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              .t('general.los', { los: losRestriction.los })
              .toString(),
            originalY: roundedValue,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            color: CHART_COLORS[index],
          });
        });
    }

    return [
      {
        name: 'availability',
        className: 'gauge-chart-border',
        innerRadius: GAUGE_INNER_RADIUS,
        radius: GAUGE_OUTER_RADIUS,
        // @ts-expect-error: Incorrect type
        data: stackGaugeChartData(data),
      },
    ];
  }

  @computed('args.summary.unavailableHotelsPercentageNumber')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    return {
      chart: {
        type: 'solidgauge',
        height: 90,
        spacingTop: 0,
        spacingRight: 0,
        spacingBottom: 0,
        spacingLeft: 0,
        animation: false,
      },
      title: {
        text: 'chart-mi-availability',
      },
      colors: CHART_COLORS,
      subtitle: {
        text: `<div class="text-dark flex flex-col">
                <h3 class="flex justify-center">${
                  // @ts-expect-error: Incorrect type
                  this.args.summary.unavailableHotelsPercentageNumber
                }%</h3>
                <p class="text-200--regular" style="text-align: center;">${this.intl.t(
                  'market_insight.availability.unavailable',
                )}</p>
              </div>`,
        useHTML: true,
        align: 'center',
        verticalAlign: 'middle',
        y: 40,
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      pane: {
        center: ['50%', '100%'],
        size: '190%',
        startAngle: -90,
        endAngle: 90,
        background: [
          {
            shape: 'arc',
            innerRadius: GAUGE_INNER_RADIUS,
            outerRadius: GAUGE_OUTER_RADIUS,
            backgroundColor: 'var(--prism-color-chart-neutral-figure-empty)',
            borderWidth: 0,
          },
        ],
      },
      yAxis: {
        min: 0,
        max: 100,
        lineWidth: 0,
        tickPositions: [],
      },
      plotOptions: {
        solidgauge: {
          animation: false,
          dataLabels: {
            enabled: false,
          },
        },
      },
    };
  }

  @computed('chartData.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get legendInfo() {
    // Data points are in reversed order for the gauge chart stacking - reverse back for the legend
    // @ts-expect-error: Incorrect type
    return [...this.chartData[0].data].reverse().map((datapoint) => ({
      name: datapoint.name,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value: datapoint.originalY,
      color: datapoint.color,
    }));
  }

  <template>
    <div class="flex flex-row items-start justify-center">
      <div class="chart-solidgauge-wrapper">
        <GeneralChartsOtaChart
          {{! @glint-expect-error: Incorrect type }}
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
          {{! @glint-expect-error: Incorrect type }}
          @onUpdateChartReference={{@onUpdateChartReference}}
        />
      </div>
      <HotelMarketInsightChartsChartSolidgaugeLegend
        @legendInfo={{this.legendInfo}}
      />
    </div>
  </template>
}
